import { InputBase } from '@mui/material';
import { darken, lighten, useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import styles from '../AdministrationStyles';

interface AdminColorPickerProps {
  label: string;
  name: string;
  value: string;
  onChange: (hex: string) => void;
}

export interface ThemeColors {
  main: string;
  light: string;
  dark: string;
}

interface ThemeColorGroupProps {
  label: string;
  name: string;
  value: string;
  onChange: (json: string) => void;
}

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/;

const rgbToHex = (color: string): string => {
  if (HEX_REGEX.test(color)) return color;
  const match = color.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
  if (match) {
    const [, r, g, b] = match;
    return `#${((1 << 24) + (Number(r) << 16) + (Number(g) << 8) + Number(b)).toString(16).slice(1)}`;
  }
  return color;
};

const safeLighten = (hex: string, coefficient: number): string => {
  try {
    return rgbToHex(lighten(hex, coefficient));
  } catch {
    return hex;
  }
};

const safeDarken = (hex: string, coefficient: number): string => {
  try {
    return rgbToHex(darken(hex, coefficient));
  } catch {
    return hex;
  }
};

const deriveColors = (main: string): ThemeColors => ({
  main,
  light: safeLighten(main, 0.2),
  dark: safeDarken(main, 0.3),
});

export const parseThemeColors = (value: string): ThemeColors => {
  if (!value) return deriveColors('#000000');
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'object' && parsed !== null && parsed.main) {
      return {
        main: HEX_REGEX.test(parsed.main) ? parsed.main : '#000000',
        light: parsed.light && HEX_REGEX.test(parsed.light) ? parsed.light : safeLighten(parsed.main || '#000000', 0.2),
        dark: parsed.dark && HEX_REGEX.test(parsed.dark) ? parsed.dark : safeDarken(parsed.main || '#000000', 0.3),
      };
    }
    if (typeof parsed === 'string' && HEX_REGEX.test(parsed)) return deriveColors(parsed);
  } catch {
    if (HEX_REGEX.test(value)) return deriveColors(value);
  }
  return deriveColors('#000000');
};

export const AdminColorPicker = ({ label, name, value, onChange }: AdminColorPickerProps): React.ReactElement => {
  const classes = styles();
  const theme = useTheme();
  const [text, setText] = useState(value);

  useEffect(() => {
    setText(value);
  }, [value]);

  const handleSwatchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const hex = e.target.value;
    setText(hex);
    onChange(hex);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = e.target.value;
    setText(raw);
    if (HEX_REGEX.test(raw)) {
      onChange(raw);
    }
  };

  const handleTextBlur = (): void => {
    if (!HEX_REGEX.test(text)) {
      setText(value);
    }
  };

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className={classes.adminTextInputLabel}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="color"
          aria-label={`${label} color picker`}
          name={`${name}-swatch`}
          value={HEX_REGEX.test(text) ? text : value}
          onChange={handleSwatchChange}
          style={{
            width: 36,
            height: 36,
            padding: 0,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 6,
            cursor: 'pointer',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            appearance: 'none',
            backgroundColor: 'transparent',
          }}
        />
        <InputBase
          name={name}
          value={text}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          className={classes.adminTextInput}
          style={{ flex: 1 }}
        />
      </div>
    </div>
  );
};

export const ThemeColorGroup = ({ label, name, value, onChange }: ThemeColorGroupProps): React.ReactElement => {
  const classes = styles();
  const [colors, setColors] = useState<ThemeColors>(() => parseThemeColors(value));

  useEffect(() => {
    setColors(parseThemeColors(value));
  }, [value]);

  const emitChange = (updated: ThemeColors): void => {
    setColors(updated);
    onChange(JSON.stringify(updated));
  };

  const handleMainChange = (hex: string): void => {
    emitChange(deriveColors(hex));
  };

  const handleLightChange = (hex: string): void => {
    emitChange({ ...colors, light: hex });
  };

  const handleDarkChange = (hex: string): void => {
    emitChange({ ...colors, dark: hex });
  };

  return (
    <div>
      <div className={classes.adminTextInputLabel} style={{ fontWeight: 500, fontSize: '0.95rem' }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <AdminColorPicker label="Main" name={`${name}-main`} value={colors.main} onChange={handleMainChange} />
        <AdminColorPicker label="Light" name={`${name}-light`} value={colors.light} onChange={handleLightChange} />
        <AdminColorPicker label="Dark" name={`${name}-dark`} value={colors.dark} onChange={handleDarkChange} />
      </div>
    </div>
  );
};
